import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import {
  FaTabletAlt,
  FaQrcode,
  FaKey,
  FaTimes,
  FaExternalLinkAlt,
  FaCopy,
  FaCheck,
  FaLock,
  FaTrashAlt,
  FaMobileAlt,
  FaSyncAlt,
  FaWifi,
  FaDownload,
  FaDesktop,
  FaApple,
  FaAndroid,
  FaWindows
} from "react-icons/fa";

const KioskLauncherModal = ({ isOpen, onClose, onLaunchDirect }) => {
  const [activeTab, setActiveTab] = useState("qr"); // "qr" | "pin" | "install" | "devices"
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [deviceName, setDeviceName] = useState("Showroom iPad #1");
  const [exitPin, setExitPin] = useState("1234");

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const generateNewSession = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/session/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          deviceName,
          exitPin
        })
      });

      const data = await res.json();
      if (res.ok && data.success && data.data) {
        setSessionData(data.data);
      } else {
        toast.error(data.message || "Failed to generate Kiosk session");
      }
    } catch (err) {
      console.error("Failed to generate Kiosk session:", err);
      toast.error("Error connecting to backend");
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveSessions = async () => {
    setLoadingSessions(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/session/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success && data.data) {
        setActiveSessions(data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch active kiosk sessions:", err);
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/session/revoke`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ sessionId })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("✓ Kiosk device session revoked and locked.");
        fetchActiveSessions();
      } else {
        toast.error(data.message || "Failed to revoke session");
      }
    } catch (err) {
      toast.error("Error revoking session");
    }
  };

  useEffect(() => {
    if (isOpen) {
      generateNewSession();
      fetchActiveSessions();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const launchUrl = sessionData
    ? `${window.location.origin}/kiosk?kioskToken=${sessionData.sessionToken}`
    : `${window.location.origin}/kiosk`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(launchUrl);
    setCopied(true);
    toast.success("Kiosk PWA Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLaunchNow = () => {
    if (onLaunchDirect) {
      onLaunchDirect(sessionData?.sessionToken);
    } else {
      window.open(launchUrl, "_blank");
    }
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[999999] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-slate-900 border border-slate-700/80 w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] text-slate-100 relative z-[1000000]">
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-cyan-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <FaTabletAlt size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-white flex items-center gap-2">
                Launch Kiosk PWA
                <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                  Dealership 2.0
                </span>
              </h2>
              <p className="text-xs text-slate-400">
                Authorize an independent tablet or open isolated Kiosk Deal Studio
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
          >
            <FaTimes size={14} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-800 bg-slate-950/30 px-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab("qr")}
            className={`py-3 px-4 text-xs font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
              activeTab === "qr"
                ? "border-cyan-500 text-cyan-400"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <FaQrcode size={12} />
            <span>Scan QR Code</span>
          </button>
          <button
            onClick={() => setActiveTab("pin")}
            className={`py-3 px-4 text-xs font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
              activeTab === "pin"
                ? "border-cyan-500 text-cyan-400"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <FaKey size={12} />
            <span>6-Digit PIN</span>
          </button>
          <button
            onClick={() => setActiveTab("install")}
            className={`py-3 px-4 text-xs font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
              activeTab === "install"
                ? "border-cyan-500 text-cyan-400"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <FaDownload size={12} />
            <span>PWA & Standalone</span>
          </button>
          <button
            onClick={() => {
              setActiveTab("devices");
              fetchActiveSessions();
            }}
            className={`py-3 px-4 text-xs font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
              activeTab === "devices"
                ? "border-cyan-500 text-cyan-400"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <FaWifi size={12} />
            <span>Active Tablets ({activeSessions.length})</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {activeTab === "qr" && (
            <div className="flex flex-col items-center text-center space-y-4">
              <p className="text-xs text-slate-300 max-w-md">
                Point any iPad or Android showroom tablet camera at this QR code to launch and install the Kiosk PWA automatically.
              </p>

              {loading ? (
                <div className="w-52 h-52 bg-slate-800/50 rounded-2xl flex items-center justify-center">
                  <span className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : sessionData ? (
                <div className="p-3 bg-white rounded-2xl shadow-xl border border-white/20">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                      launchUrl
                    )}`}
                    alt="Kiosk PWA Pairing QR"
                    className="w-48 h-48 rounded-xl"
                  />
                </div>
              ) : (
                <button
                  onClick={generateNewSession}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-xs rounded-xl"
                >
                  Generate QR
                </button>
              )}

              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Authorized Salesperson:</span>
                <span className="font-bold text-white">
                  {user.firstName ? `${user.firstName} ${user.lastName || ""}` : user.name || "You"}
                </span>
              </div>
            </div>
          )}

          {activeTab === "pin" && (
            <div className="flex flex-col items-center text-center space-y-5">
              <p className="text-xs text-slate-300 max-w-md">
                If the Kiosk app is already opened on the tablet, type this 6-digit code on the tablet screen to pair instantly.
              </p>

              {loading ? (
                <span className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <div className="bg-slate-950 border-2 border-cyan-500/40 px-8 py-5 rounded-2xl shadow-inner">
                  <span className="text-4xl font-mono font-black tracking-widest text-cyan-400">
                    {sessionData?.pairingCode || "------"}
                  </span>
                </div>
              )}

              <div className="text-xs text-slate-400 space-y-1">
                <p>
                  Exit / Lock PIN for this session:{" "}
                  <span className="font-bold text-emerald-400">{sessionData?.exitPin || "1234"}</span>
                </p>
                <p className="text-[11px] text-slate-500">
                  (Salesperson PIN required to exit kiosk back to dashboard)
                </p>
              </div>
            </div>
          )}

          {activeTab === "install" && (
            <div className="space-y-4 text-xs text-slate-300">
              <div className="p-4 rounded-2xl bg-cyan-950/30 border border-cyan-500/30 space-y-2">
                <h3 className="font-bold text-cyan-300 flex items-center gap-2 text-sm">
                  <FaTabletAlt /> PWA Installation & Hardware Kiosk Setup
                </h3>
                <p className="text-slate-300 text-xs leading-relaxed">
                  To turn any iPad or showroom display into a dedicated, chrome-free Kiosk without URL bars:
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div className="p-3.5 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-2">
                  <div className="font-bold text-white flex items-center gap-2">
                    <FaApple className="text-cyan-400" /> iPad / iOS Safari
                  </div>
                  <ol className="list-decimal list-inside space-y-1 text-[11px] text-slate-400">
                    <li>Scan the QR code in Safari.</li>
                    <li>Tap <strong className="text-slate-200">Share &gt; Add to Home Screen</strong>.</li>
                    <li>Enable <strong className="text-slate-200">Guided Access</strong> (Triple-click power button) to lock the tablet.</li>
                  </ol>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-2">
                  <div className="font-bold text-white flex items-center gap-2">
                    <FaWindows className="text-cyan-400" /> Chrome / Windows Kiosk
                  </div>
                  <ol className="list-decimal list-inside space-y-1 text-[11px] text-slate-400">
                    <li>Open Kiosk link in Chrome / Edge.</li>
                    <li>Click <strong className="text-slate-200">Install App</strong> in address bar.</li>
                    <li>Or launch with: <code className="text-cyan-200 font-mono text-[10px]">--kiosk --app=URL</code></li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {activeTab === "devices" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-300">
                  Showroom Tablets Currently Paired
                </span>
                <button
                  onClick={fetchActiveSessions}
                  className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 font-semibold"
                >
                  <FaSyncAlt size={10} /> Refresh
                </button>
              </div>

              {loadingSessions ? (
                <div className="text-center py-6 text-slate-400 text-xs">
                  Loading active devices...
                </div>
              ) : activeSessions.length === 0 ? (
                <div className="text-center py-8 bg-slate-950/40 rounded-2xl border border-slate-800/80">
                  <FaTabletAlt size={24} className="text-slate-600 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">No other active tablets paired right now.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {activeSessions.map((s) => (
                    <div
                      key={s._id}
                      className="p-3.5 bg-slate-950/60 border border-slate-800 rounded-xl flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                          <FaTabletAlt size={14} />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-white">
                            {s.deviceName || "Showroom iPad"}
                          </div>
                          <div className="text-[10px] text-slate-400">
                            Last Active: {new Date(s.lastActiveAt).toLocaleTimeString()} · Code:{" "}
                            {s.pairingCode}
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => handleRevokeSession(s._id)}
                        className="px-3 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5"
                      >
                        <FaLock size={10} /> Revoke / Lock
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/70 flex flex-col sm:flex-row items-center justify-between gap-3">
          <button
            onClick={handleCopyLink}
            className="w-full sm:w-auto px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-xl transition-colors flex items-center justify-center gap-2 border border-slate-700"
          >
            {copied ? <FaCheck className="text-emerald-400" /> : <FaCopy />}
            <span>{copied ? "Link Copied!" : "Copy Kiosk Link"}</span>
          </button>

          <button
            onClick={handleLaunchNow}
            className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-cyan-500 via-indigo-500 to-blue-600 hover:from-cyan-400 hover:to-indigo-400 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 cursor-pointer"
          >
            <span>Launch on this Screen</span>
            <FaExternalLinkAlt size={11} />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default KioskLauncherModal;
